import React, { useMemo } from "react";
import { Box } from "@mui/material";
import AreaChart from "../../AreaChart";
import HorizontalBarList from "../../HorizontalBarList";
import InfoCard from "../../InfoCard";
import {
  SupplierOverviewResponse,
  SupplierWithMostContractsItem,
  MonthlyData,
} from "../../../../types/suppliers";
import { FilterValues } from "../../FilterModal";
import styles from "./analyticsDashboard.module.scss";

// Utility function to transform monthly data with year handling
const transformMonthlyData = (data: MonthlyData[]): Array<{ month: string; count: number }> => {
  if (!data || data.length === 0) return [];

  // Check if data spans multiple years
  const years = [...new Set(data.map((item) => item.year).filter(Boolean))];
  const hasMultipleYears = years.length > 1;

  return data.map((item) => ({
    month: hasMultipleYears && item.year ? `${item.month} ${item.year}` : item.month,
    count: item.count,
  }));
};

interface AnalyticsDashboardProps {
  overviewData: SupplierOverviewResponse;
  suppliersWithMostContracts: SupplierWithMostContractsItem[];
  selectedYear?: string;
  filters?: FilterValues;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  overviewData,
  suppliersWithMostContracts,
  selectedYear,
  filters = {},
}) => {
  const formatCurrency = (value: number): string => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(1)}K`;
    }
    return `$${value}`;
  };

  // Check if any date filters are applied
  const hasDateFilters = !!(filters.start_date || filters.end_date);

  // Transform data once with memoization for performance
  const transformedInitiatedData = useMemo(
    () => transformMonthlyData(overviewData.initiatedByMonth || []),
    [overviewData.initiatedByMonth]
  );

  const transformedApprovalData = useMemo(
    () => transformMonthlyData(overviewData.approvalByMonth || []),
    [overviewData.approvalByMonth]
  );

  return (
    <>
      <Box className={styles.fullWidthChart}>
        <AreaChart
          title="Initiated by Month"
          data={transformedInitiatedData}
          xAxisKey="month"
          areas={[{ dataKey: "count", color: "#5b6b9e", label: "Initiated" }]}
          showThisYear={!hasDateFilters}
        />
      </Box>

      <Box className={styles.fullWidthChart}>
        <AreaChart
          title="Approval by Month"
          data={transformedApprovalData}
          xAxisKey="month"
          areas={[{ dataKey: "count", color: "#00d2aa", label: "Approved" }]}
          showThisYear={!hasDateFilters}
        />
      </Box>

      <Box className={styles.listsSection}>
        <HorizontalBarList
          title="Top Spend Suppliers (USD)"
          data={overviewData.topSpendSuppliers || []}
          labelKey="supplierName"
          valueKey="totalSpend"
          valueFormatter={formatCurrency}
        />
        <HorizontalBarList
          title="Suppliers with Most Contracts"
          data={suppliersWithMostContracts || []}
          labelKey="supplierName"
          valueKey="contractsCount"
        />
      </Box>

      {/* <Box className={styles.infoCardsSection}>
        <InfoCard
          type="region"
          title="Suppliers in UAE Region"
          value={overviewData.primaryCards.uaeRegionSuppliers?.count || 0}
          subtitle={
            overviewData.primaryCards.uaeRegionSuppliers?.label ||
            "UAE Region Suppliers"
          }
        />
        <InfoCard
          type="new_suppliers"
          title="Registered in 2026"
          value={
            overviewData.primaryCards.registeredSuppliersInSpecificYear
              ?.count || 0
          }
          subtitle={
            overviewData.primaryCards.registeredSuppliersInSpecificYear
              ?.label || "New suppliers"
          }
        />
      </Box> */}
    </>
  );
};

export default AnalyticsDashboard;
